import { useMemo } from 'react';

import {
  instanceOverrides,
  prefabInstanceOf,
  prefabRef,
  variantBase,
  type ExpandedScene,
  type OverrideItem,
} from '../../lib/prefab.js';
import type { EditorCommand, EntityDesc } from '../../lib/scene-doc.js';
import { useAssets } from '../../state/assets.js';
import { usePrefabs } from '../../state/prefabs.js';
import { useScene } from '../../state/scene.js';
import { MenuButton, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import type { InspectorTarget } from './inspector-context.js';

/**
 * Prefab parts of the Inspector: the instance header (Open / Select /
 * Overrides with Apply and Revert, all or per property) and the edit target
 * that marks overridden properties.
 */

/** The instance owning `id` (itself when it is an instance root). */
export function instanceHost(doc: { entities: EntityDesc[] }, id: string): EntityDesc | null {
  const own = doc.entities.find((e) => e.id === id);
  if (own && prefabRef(own)) return own;
  return prefabInstanceOf(doc, id)?.host ?? null;
}

function matching(items: readonly OverrideItem[], id: string, path: string): OverrideItem[] {
  return items.filter((i) => i.id === id && (i.path === path || i.path.startsWith(`${path}.`) || path.startsWith(`${i.path}.`)));
}

/** Inspector target for an entity of a prefab instance (null for plain entities). */
export function usePrefabTarget(id: string | null, expanded: ExpandedScene | null): InspectorTarget | null {
  const doc = useScene((s) => s.doc);
  const apply = useScene((s) => s.apply);
  const version = usePrefabs((s) => s.version);
  return useMemo(() => {
    void version;
    if (!doc || !id) return null;
    const host = instanceHost(doc, id);
    if (!host) return null;
    const x = expanded?.instances.get(host.id) ?? null;
    const items = instanceOverrides(host, x, usePrefabs.getState().loader());
    const prefabs = usePrefabs.getState();
    const path = prefabRef(host)!.path;
    return {
      apply: (c: EditorCommand) => void apply(c),
      mark: (eid, p) => {
        if (matching(items, eid, p).length > 0) return 'overridden';
        if (eid === host.id && x && p.startsWith('components.') && x.inherited.includes(p.split('.')[1]!)) return 'inherited';
        return null;
      },
      fieldMenu: (eid, p) => {
        const hits = matching(items, eid, p);
        if (hits.length === 0) return [];
        return [
          { id: 'apply', label: `Apply to Prefab '${path.replace(/^.*\//, '')}'`, icon: 'arrowUp', run: () => void prefabs.applyOverrides(host.id, hits) },
          { id: 'revert', label: 'Revert', icon: 'undo', run: () => prefabs.revertOverrides(host.id, hits) },
        ];
      },
      componentMenu: (eid, key) => {
        const hits = matching(items, eid, `components.${key}`);
        if (hits.length === 0) return [];
        return [
          { id: 'sep-prefab', label: '', separator: true },
          { id: 'apply-c', label: 'Apply Component to Prefab', icon: 'arrowUp', run: () => void prefabs.applyOverrides(host.id, hits) },
          { id: 'revert-c', label: 'Revert Component Overrides', icon: 'undo', run: () => prefabs.revertOverrides(host.id, hits) },
        ];
      },
    };
  }, [doc, id, expanded, apply, version]);
}

/** Header shown above the components of a prefab instance or one of its entities. */
export function PrefabHeader({ id, expanded }: { id: string; expanded: ExpandedScene | null }) {
  const doc = useScene((s) => s.doc);
  const version = usePrefabs((s) => s.version);
  const data = useMemo(() => {
    void version;
    if (!doc) return null;
    const host = instanceHost(doc, id);
    if (!host) return null;
    const x = expanded?.instances.get(host.id) ?? null;
    const items = instanceOverrides(host, x, usePrefabs.getState().loader());
    const ref = prefabRef(host)!;
    const base = variantBase(usePrefabs.getState().docs[ref.path]);
    return { host, x, items, ref, base };
  }, [doc, id, expanded, version]);
  if (!data) return null;
  const { host, x, items, ref, base } = data;
  const prefabs = usePrefabs.getState();
  const isRoot = host.id === id;
  const missing = (x?.missing.length ?? 0) > 0;
  const overrideItems: MenuItem[] = items.length
    ? [
        { id: 'h', label: `${items.length} override${items.length === 1 ? '' : 's'}`, heading: true },
        ...items.flatMap((item, i): MenuItem[] => [
          { id: `i${i}`, label: item.label, icon: item.removed ? 'trash' : 'edit', disabled: true },
          { id: `a${i}`, label: '    Apply', icon: 'arrowUp', run: () => void prefabs.applyOverrides(host.id, [item]) },
          { id: `r${i}`, label: '    Revert', icon: 'undo', run: () => prefabs.revertOverrides(host.id, [item]) },
        ]),
        { id: 'sep', label: '', separator: true },
        { id: 'apply-all', label: 'Apply All', icon: 'arrowUp', run: () => void prefabs.applyOverrides(host.id) },
        { id: 'revert-all', label: 'Revert All', icon: 'undo', danger: true, run: () => prefabs.revertOverrides(host.id) },
      ]
    : [{ id: 'none', label: 'No overrides', disabled: true }];
  return (
    <div className={`prefab-header${missing ? ' is-missing' : ''}`}>
      <Icon name="prefab" size={14} />
      <div className="prefab-header-text">
        <span className="prefab-header-title">
          {isRoot ? 'Prefab instance' : `In prefab instance '${host.name || host.id}'`}
          {missing && <strong className="prefab-missing"> · missing</strong>}
        </span>
        <button type="button" className="link mono prefab-path" title="Select the prefab in Assets" onClick={() => useAssets.getState().pingAsset(ref.path)}>
          {ref.path}
        </button>
        {base && <span className="muted mono prefab-base">variant of {base}</span>}
      </div>
      <div className="prefab-header-actions">
        <button type="button" className="tb-btn" title="Edit the prefab on its own" onClick={() => void prefabs.openStage(ref.path, 'isolated')}>
          Open
        </button>
        <button type="button" className="tb-btn" title="Edit the prefab with this scene around it" onClick={() => void prefabs.openStage(ref.path, 'context')}>
          In Context
        </button>
        {!isRoot && (
          <button type="button" className="tb-btn" title="Select the instance root" onClick={() => useScene.getState().select([host.id])}>
            Root
          </button>
        )}
        <MenuButton items={overrideItems} className={`tb-btn${items.length ? ' is-active' : ''}`} title="Overrides of this instance">
          Overrides{items.length ? ` (${items.length})` : ''} <Icon name="chevronDown" size={11} />
        </MenuButton>
      </div>
    </div>
  );
}
